import { groupBy, mapValues } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { FeaturePatternTrussContainer } from '@paragon/contract-library';
import { Polygon2D } from '@paragon/geometry-library';
import { calculateFloorPolyline2DShape } from '@paragon/layout';
import { notNullish } from '@paragon/utilities';

import {
  getAllBearingEnvelopesDictionary,
  getAllFeaturePatternContainersDictionary,
  getVisibleLevelGuids,
  NULL_LEVEL_GUID_KEY,
} from './layoutDataSelectors';

/**
 * Calculates the 2D polygons of feature pattern containers in the open layout, grouped by their level.
 *
 * An additional special key 'visible' is always present which contains polygons from all visible levels, and also works
 * for layouts without any levels.
 */
export const getFeaturePatternContainerShapes = createSelector(
  getVisibleLevelGuids,
  getAllFeaturePatternContainersDictionary,
  getAllBearingEnvelopesDictionary,

  (visibleLevels, featurePatternContainerDictionary, bearingEnvelopes) => {
    function calculateShapes(containers: FeaturePatternTrussContainer[]) {
      return containers
        .map((container) => calculateFloorPolyline2DShape(container.shape, bearingEnvelopes))
        .filter(notNullish);
    }

    const visibleContainers = Object.values(featurePatternContainerDictionary).filter((container) =>
      visibleLevels.has(container.levelGuid ?? null),
    );

    const calculatedShapesByLevel = Object.entries(
      mapValues(
        groupBy(visibleContainers, ({ levelGuid }) => levelGuid ?? NULL_LEVEL_GUID_KEY),
        calculateShapes,
      ),
    );

    const entries: (readonly [string, Polygon2D[]])[] = [
      ['visible', calculateShapes(visibleContainers)],
      ...calculatedShapesByLevel,
    ];

    return Object.fromEntries(entries);
  },
);
